import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { ArrowLeft, FileText, Send, Calendar, MapPin, User, Hash, Zap, Settings, X } from 'lucide-react';
import { toast } from 'sonner';

const RequestForm = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    request_type: [],
    priority: 'medium', // Back to array for multiple selections
    case_number: '',
    priority: 'medium',
    // Enhanced fields
    incident_date: '',
    incident_time: '',
    incident_location: '',
    officer_names: '',
    vehicle_info: '',
    additional_details: '',
    // Media delivery options
    delivery_format: 'digital',
    delivery_method: 'download',
    payment_method: 'check'
  });
  const [costAcknowledged, setCostAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  
  const { API, user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Request templates for quick start
  const requestTemplates = {
    police_report: {
      title: 'Police Report Request',
      description: 'I am requesting a copy of a police report for the following incident:\n\nDate of Incident: [DATE]\nLocation: [LOCATION]\nCase/Report Number: [NUMBER]\nReason for Request: [REASON]\n\nPlease provide a certified copy of the complete report including any supplemental reports.',
      priority: 'medium'
    },
    body_camera: {
      title: 'Body Camera Footage Request', 
      description: 'I am requesting body camera footage from the following incident:\n\nDate: [DATE]\nTime: [TIME]\nLocation: [LOCATION]\nOfficer Badge Numbers: [NUMBERS]\n\nPlease provide the footage in digital format.',
      priority: 'high',
      request_type: 'body_cam_footage'
    },
    traffic_citation: {
      title: 'Traffic Citation Records',
      description: 'I am requesting records related to a traffic citation:\n\nCitation Number: [NUMBER]\nDate of Citation: [DATE]\nLocation: [LOCATION]\nVehicle License: [LICENSE]\n\nPlease provide all related documentation.',
      priority: 'low'
    }
  };

  // Check if user has special privileges
  const isSpecialRequester = user && (
    user.role === 'admin' || 
    user.role === 'staff' ||
    user.email?.includes('@law.') ||
    user.email?.includes('@court.') ||
    user.email?.includes('@attorney.') ||
    user.email?.includes('@police.') ||
    user.email?.includes('@prosecutor.') ||
    user.email?.includes('.gov')
  );

  const applyTemplate = (templateKey) => {
    const template = requestTemplates[templateKey];
    if (template) {
      setFormData(prev => ({
        ...prev,
        title: template.title,
        description: template.description,
        priority: template.priority,
        request_type: template.request_type ? [template.request_type] : prev.request_type
      }));
      toast.success(`Applied ${template.title} template`);
    }
  };

  const requestTypes = [
    { value: 'incident_report', label: 'Incident Report' },
    { value: 'police_report', label: 'Police Report' },
    { value: 'body_cam_footage', label: 'Body Camera Footage' },
    { value: 'dashcam_footage', label: 'Dash Camera Footage' },
    { value: '911_audio', label: '911 Audio Recording' },
    { value: 'photos', label: 'Photos/Images' },
    { value: 'documents', label: 'Documents/PDFs' },
    { value: 'traffic_citation', label: 'Traffic Citation' },
    { value: 'arrest_record', label: 'Arrest Record' },
    { value: 'other', label: 'Other Records' }
  ];

  const deliveryFormats = [
    { value: 'digital', label: 'Digital Download' },
    { value: 'cd', label: 'CD/DVD' },
    { value: 'usb', label: 'USB Drive' },
    { value: 'print', label: 'Printed Copy' },
    { value: 'certified', label: 'Certified Copy' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
  };

  const uploadFiles = async (requestId) => {
    if (selectedFiles.length === 0) return;

    setUploadingFiles(true);
    console.log(`Uploading ${selectedFiles.length} files to request ${requestId}`);
    
    const uploadPromises = selectedFiles.map(async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API}/upload/${requestId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        console.log(`Successfully uploaded ${file.name}:`, response.data);
        return { success: true, fileName: file.name, data: response.data };
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        return { success: false, fileName: file.name, error: error.message };
      }
    });

    const results = await Promise.all(uploadPromises);
    const failedUploads = results.filter(result => !result.success);
    const successfulUploads = results.filter(result => result.success);
    
    console.log(`Upload results: ${successfulUploads.length} successful, ${failedUploads.length} failed`);
    
    if (failedUploads.length > 0) {
      toast.error(`Failed to upload: ${failedUploads.map(f => f.fileName).join(', ')}`);
    } else if (results.length > 0) {
      toast.success(`Successfully uploaded ${successfulUploads.length} file(s)`);
    }

    setUploadingFiles(false);
  };

  // Function to replace placeholders in description with actual form data
  const processDescription = (description) => {
    // Ensure we have a valid string input
    if (!description || typeof description !== 'string') {
      return '';
    }
    
    let processedDescription = description;
    
    // Map delivery format values to user-friendly text
    const deliveryFormatText = {
      'digital': 'digital format',
      'cd_dvd': 'CD/DVD format',
      'usb_drive': 'USB drive format',
      'cloud_link': 'secure cloud link'
    };
    
    // Replace common placeholders with actual form data
    const replacements = {
      '[DATE]': formData.incident_date || '[DATE - Please specify]',
      '[TIME]': formData.incident_time || '[TIME - Please specify]',
      '[LOCATION]': formData.incident_location || '[LOCATION - Please specify]',
      '[NUMBERS]': formData.officer_names || formData.case_number || '[NUMBERS - Please specify]',
      '[NUMBER]': formData.case_number || '[NUMBER - Please specify]',
      '[LICENSE]': formData.vehicle_info || '[LICENSE - Please specify]',
      '[REASON]': 'Legal proceedings/Personal records',
      '[digital format]': deliveryFormatText[formData.delivery_format] || 'digital format',
      '[DELIVERY_FORMAT]': deliveryFormatText[formData.delivery_format] || 'digital format'
    };

    // Apply replacements
    Object.entries(replacements).forEach(([placeholder, replacement]) => {
      // Ensure replacement is also a string
      const safeReplacement = String(replacement || '');
      processedDescription = processedDescription.replace(new RegExp(placeholder.replace(/[[\]]/g, '\\$&'), 'g'), safeReplacement);
    });

    return processedDescription;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Basic validation
      if (!formData.title || !formData.description || formData.request_type.length === 0) {
        const errorMsg = 'Please fill in all required fields.';
        setError(errorMsg);
        toast.error(errorMsg);
        setLoading(false);
        return;
      }

      // Special validation for body camera requests
      if (formData.request_type.includes('body_cam_footage')) {
        if (!costAcknowledged && !isSpecialRequester) {
          const errorMsg = 'Please acknowledge the processing fees for body camera footage.';
          setError(errorMsg);
          toast.error(errorMsg);
          setLoading(false);
          return;
        }
        
        if (!formData.incident_date || !formData.incident_time || !formData.incident_location || !formData.officer_names) {
          const errorMsg = 'Please fill in all required fields for body camera footage requests (Date, Time, Location, Officer names).';
          setError(errorMsg);
          toast.error(errorMsg);
          setLoading(false);
          return;
        }
      }

      // Process the description to replace placeholders with actual data
      const processedDescription = processDescription(formData.description);

      // Prepare submission data with backend compatibility
      const submissionData = {
        ...formData,
        description: processedDescription,
        cost_acknowledged: costAcknowledged,
        // Backend expects single request_type, so send the first one selected
        // or you can send comma-separated string if backend supports it
        request_type: formData.request_type.length > 0 ? formData.request_type[0] : ''
      };

      const response = await axios.post(`${API}/requests`, submissionData);
      
      // Upload files if any were selected
      if (selectedFiles.length > 0) {
        await uploadFiles(response.data.id);
      }
      
      toast.success('Request submitted successfully!');
      navigate(`/request/${response.data.id}`);
    } catch (error) {
      let errorMessage = 'Failed to submit request. Please try again.';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Handle validation errors array
          errorMessage = detail.map(err => err.msg || err.message || 'Validation error').join(', ');
        } else if (typeof detail === 'object') {
          // Handle single validation error object
          errorMessage = detail.msg || detail.message || 'Validation error occurred';
        }
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/dashboard')}
            className="mb-4 text-slate-600 hover:text-blue-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="text-center">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Submit Records Request</h1>
            <p className="text-slate-600">Request public records and documentation</p>
          </div>
        </div>

        <Card className="shadow-xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Request Details
            </CardTitle>
            <CardDescription>
              Fill out the form below to submit your records request
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Quick Templates */}
            <div className="mb-6">
              <Label className="text-sm font-medium mb-3 block">Quick Templates</Label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(requestTemplates).map(([key, template]) => (
                  <Button
                    key={key}
                    variant="outline"
                    size="sm"
                    onClick={() => applyTemplate(key)}
                    className="text-xs"
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    {template.title}
                  </Button>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert className="border-red-200 bg-red-50">
                  <AlertDescription className="text-red-700">
                    {typeof error === 'string' ? error : 'An error occurred while processing your request.'}
                  </AlertDescription>
                </Alert>
              )}

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Request Title</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Create title for your request"
                    className="mt-1 placeholder:text-gray-400"
                  />
                </div>

                <div>
                  <Label htmlFor="request_type" className="required">Request Type</Label>
                  <div className="mt-1 space-y-2 max-h-40 overflow-y-auto border rounded-md p-2">
                    {requestTypes.map((type) => (
                      <label key={type.value} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-1 rounded">
                        <input
                          type="checkbox"
                          checked={formData.request_type.includes(type.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                request_type: [...prev.request_type, type.value]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                request_type: prev.request_type.filter(t => t !== type.value)
                              }));
                            }
                          }}
                          className="rounded"
                        />
                        <span className="text-sm">{type.label}</span>
                      </label>
                    ))}
                  </div>
                  {formData.request_type.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selected: {formData.request_type.map(t => requestTypes.find(rt => rt.value === t)?.label).join(', ')}
                    </p>
                  )}
                </div>
              </div>

              {/* Details Section */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="case_number">Case/Report Number</Label>
                    <Input
                      id="case_number"
                      name="case_number"
                      value={formData.case_number}
                      onChange={handleInputChange}
                      placeholder="If known"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="incident_date">Incident Date</Label>
                    <Input
                      id="incident_date"
                      name="incident_date"
                      type="date"
                      value={formData.incident_date}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="incident_time">Incident Time</Label>
                    <Input
                      id="incident_time"
                      name="incident_time"
                      type="time"
                      value={formData.incident_time}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="incident_location">Incident Location</Label>
                    <Input
                      id="incident_location"
                      name="incident_location"
                      value={formData.incident_location}
                      onChange={handleInputChange}
                      placeholder="Street address or general area"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="officer_names">Officer Names/Badge Numbers</Label>
                    <Input
                      id="officer_names"
                      name="officer_names"
                      value={formData.officer_names}
                      onChange={handleInputChange}
                      placeholder="If known"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label htmlFor="vehicle_info">Vehicle/License Plate/Make/Model</Label>
                    <Input
                      id="vehicle_info"
                      name="vehicle_info"
                      value={formData.vehicle_info}
                      onChange={handleInputChange}
                      placeholder=""
                      className="mt-1"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <Label htmlFor="additional_details">Additional Details (i.e. involved party)</Label>
                    <Textarea
                      id="additional_details"
                      name="additional_details"
                      value={formData.additional_details}
                      onChange={handleInputChange}
                      placeholder="Any other relevant information..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Detailed Description */}
              <div className="border-t pt-6">
                <div className="md:col-span-2">
                  <Label htmlFor="description" className="required">Detailed Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Provide detailed information about your request..."
                    rows={4}
                    required
                    className="mt-1"
                  />
                  
                  {/* Show real-time preview with filled placeholders */}
                  {formData.description && formData.description.includes('[') && (
                    <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded">
                      <Label className="text-xs font-medium text-green-800 mb-2 block">✅ Preview (how it will appear):</Label>
                      <p className="text-sm text-green-900 whitespace-pre-wrap">
                        {processDescription(formData.description)}
                      </p>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.description && formData.description.includes('[') ? 
                      'Fill in the Details section above to automatically replace placeholders' : 
                      'Use templates above or describe your request'
                    }
                  </p>
                </div>
              </div>

              {/* Cost Acknowledgment for Body Camera Footage */}
              {formData.request_type.includes('body_cam_footage') && !isSpecialRequester && (
                <div className="border-t pt-6">
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <input 
                        type="checkbox" 
                        id="cost_acknowledgment" 
                        checked={costAcknowledged}
                        onChange={(e) => setCostAcknowledged(e.target.checked)}
                        className="mt-1 rounded"
                      />
                      <label htmlFor="cost_acknowledgment" className="text-sm text-amber-800">
                        <strong>Cost Acknowledgment Required:</strong> I understand that body camera footage requests may incur processing fees. 
                        Standard fees apply for media preparation, copying, and administrative processing as outlined in the department's fee schedule.
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Documents & Files - Drag & Drop Upload */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Documents & Files</h3>
                <p className="text-sm text-gray-600 mb-4">Upload and manage files related to this request</p>
                
                <div className="border-2 border-dashed border-gray-300 rounded-lg">
                  <div className="p-6">
                    <div
                      className={`text-center p-8 rounded-lg transition-all cursor-pointer ${
                        'bg-slate-50 hover:bg-slate-100'
                      } ${uploadingFiles ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => document.getElementById('file-upload-input').click()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const files = Array.from(e.dataTransfer.files);
                        setSelectedFiles(prev => [...prev, ...files]);
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDragEnter={(e) => e.preventDefault()}
                    >
                      <input
                        id="file-upload-input"
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.txt,.zip"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <div className="w-12 h-12 mx-auto mb-4 text-slate-400">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <path d="M17 8l-5-5-5 5"></path>
                          <path d="M12 3v12"></path>
                        </svg>
                      </div>
                      <div>
                        <p className="text-slate-600 font-medium mb-2">
                          Drop files here or click to upload
                        </p>
                        <p className="text-sm text-slate-500">
                          Maximum file size: 10MB. Multiple files supported.
                        </p>
                      </div>
                    </div>

                    {/* Selected Files Preview */}
                    {selectedFiles.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <p className="text-sm font-medium text-gray-700">Attached Files ({selectedFiles.length}):</p>
                        {selectedFiles.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border">
                            <div className="flex items-center gap-3 flex-1">
                              <FileText className="w-4 h-4 text-blue-500" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 truncate">{file.name}</p>
                                <div className="flex items-center gap-4 text-sm text-slate-500">
                                  <span>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                                  <span>•</span>
                                  <span>{file.type || 'Unknown type'}</span>
                                </div>
                              </div>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newFiles = selectedFiles.filter((_, i) => i !== index);
                                setSelectedFiles(newFiles);
                              }}
                              className="ml-3 text-red-500 hover:text-red-700"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Upload Guidelines */}
                  <div className="bg-blue-50 border-t border-blue-200 p-4 rounded-b-lg">
                    <div className="flex items-start gap-2">
                      <div className="w-4 h-4 text-blue-600 mt-0.5">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"></path>
                        </svg>
                      </div>
                      <div>
                        <p className="text-xs text-blue-800 font-medium">File Guidelines:</p>
                        <p className="text-xs text-blue-700">
                          Supported formats include PDF, images (JPG, PNG), documents (DOC, DOCX), 
                          text files, and archives (ZIP). Maximum file size is 10MB per file. 
                          All uploads are securely stored and only accessible to authorized personnel.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Delivery Options */}
              <div className="border-t pt-6">
                <h3 className="font-medium mb-4">Delivery Preferences</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="delivery_format">Delivery Format</Label>
                    <Select value={formData.delivery_format} onValueChange={(value) => handleSelectChange('delivery_format', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {deliveryFormats.map((format) => (
                          <SelectItem key={format.value} value={format.value}>
                            {format.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="delivery_method">Delivery Method</Label>
                    <Select value={formData.delivery_method} onValueChange={(value) => handleSelectChange('delivery_method', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="download">Download Link</SelectItem>
                        <SelectItem value="pickup">In-Person Pickup</SelectItem>
                        <SelectItem value="mail">Mail</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Payment Method Section */}
                <div className="border-t pt-6">
                  <h3 className="font-medium mb-4">Payment Method</h3>
                  <div>
                    <Label htmlFor="payment_method">Payment Method</Label>
                    <Select value={formData.payment_method} onValueChange={(value) => handleSelectChange('payment_method', value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="check">Check</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    {formData.payment_method === 'check' && (
                      <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          <strong>Check Payment Instructions:</strong><br />
                          Make checks payable to: <strong>Shaker Heights Police</strong><br />
                          Mail to: 3355 Lee Rd, Shaker Heights, OH 44120
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Cost Acknowledgment for Body Camera Footage */}
              {formData.request_type.includes('body_cam_footage') && !isSpecialRequester && (
                <div className="border-t pt-6">
                  <Alert className="border-amber-200 bg-amber-50">
                    <AlertDescription className="text-amber-700">
                      <div className="flex items-start gap-3">
                        <input 
                          type="checkbox" 
                          id="cost_acknowledgment" 
                          checked={costAcknowledged}
                          onChange={(e) => setCostAcknowledged(e.target.checked)}
                          className="mt-1"
                        />
                        <label htmlFor="cost_acknowledgment" className="text-sm">
                          <strong>Cost Acknowledgment Required:</strong> I understand that body camera footage requests may incur processing fees. 
                          Standard fees apply for media preparation, copying, and administrative processing as outlined in the department's fee schedule.
                        </label>
                      </div>
                    </AlertDescription>
                  </Alert>
                </div>
              )}

              {/* Special Requester Badge */}
              {isSpecialRequester && (
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="bg-blue-50 text-blue-700">
                    <User className="w-3 h-3 mr-1" />
                    Special Requester - Expedited Processing
                  </Badge>
                </div>
              )}

              {/* Submit Button */}
              <div className="flex justify-end space-x-4 pt-6 border-t">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => navigate('/dashboard')}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={loading || uploadingFiles}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {loading || uploadingFiles ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {uploadingFiles ? 'Uploading files...' : 'Submitting...'}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Submit Request
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RequestForm;